import React from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import { COLOR_TEXT_SECONDARY, COLOR_WHITE } from "../common/colors";
import {
  didProfileCompleteLesson,
  isInTrialMode,
  Lesson,
  NextLessonResponse,
} from "../common/api";
import { ApiContext } from "../common/context/ApiContext";
import { Account, AccountContext } from "../common/context/AccountContext";
import { useLoadingIndicator } from "../common/context/LoadingIndicatorContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlay } from "@fortawesome/pro-solid-svg-icons";
import {
  faCircleCheck,
  faCopy,
  faRotateLeft,
} from "@fortawesome/pro-regular-svg-icons";
import { useTranslation } from "react-i18next";
import {
  BackNavRow,
  CallToActionButton,
  DetailsTitle,
  PlayButton,
} from "../common/CommonStyles";
import { LessonExecutionState } from "./LessonPage";
import {
  AwardStickerRail,
  railStickerAwards,
} from "../components/lesson/LessonAwards";
import { PrintablesModal } from "../components/lesson/PrintablesModal";
import {
  LeftCornerDecoration,
  LessonItemBody,
} from "../components/LessonItemBody";
import {
  AnalyticsContext,
  LessonEndFinishButtonTapEvent,
  LessonEndNextLessonImageTapEvent,
  LessonEndPlayAgainButtonTapEvent,
  LessonEndPlayNextButtonTapEvent,
  LessonMaterialsPostLessonButtonTapEvent,
  ViewLessonEndEvent,
} from "../common/context/AnalyticsContext";
import { TabEnum } from "../common/context/TabsContext";
import {
  SURFACE_CARD_RADIUS,
  SURFACE_CARD_RADIUS_LARGE,
} from "../common/constants";
import { SPACE_SM, SPACE_MD, SPACE_LG, SPACE_XL } from "../common/spacing";
import { ELEVATION_1 } from "../common/elevation";

interface Props {
  lesson: Lesson;
  setLessonExecutionState: (LessonExecutionState: LessonExecutionState) => void;
  account: Account;
}

const CONTENT_MAX_WIDTH = 456;
const GEMS_HERO_MAX_HEIGHT = 97;
const TABLET_QUERY = "@media (min-width: 600px)";

const Column = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: ${CONTENT_MAX_WIDTH}px;
  margin: 0 auto;
  padding: ${SPACE_XL}px 0;
`;

const EarnedSummary = styled.div`
  margin-top: ${SPACE_SM}px;
  color: ${COLOR_TEXT_SECONDARY};
  font-size: 14px;
  font-weight: 500;
  text-align: center;
`;

const GemsHero = styled.img`
  display: block;
  max-width: 100%;
  max-height: ${GEMS_HERO_MAX_HEIGHT}px;
  margin-top: ${SPACE_MD}px;
`;

const Awards = styled(AwardStickerRail)`
  width: 100%;
  margin-top: ${SPACE_MD}px;
`;

const CompletionBurst = styled.img`
  display: block;
  padding: ${SPACE_SM}px;
  object-fit: contain;
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE_LG}px;
  width: 100%;
  margin: ${SPACE_XL}px 0;

  ${TABLET_QUERY} {
    margin: ${SPACE_MD}px 0;
  }
`;

const ActionRow = styled.div`
  display: flex;
  gap: ${SPACE_LG}px;
`;

const PillButton = styled(CallToActionButton)`
  max-width: none;
  padding-left: ${SPACE_LG}px;
  padding-right: ${SPACE_LG}px;
  background-color: ${COLOR_WHITE};
  box-shadow: ${ELEVATION_1};
`;

const NextLessonCard = styled.div`
  width: 100%;
  margin-top: ${SPACE_XL}px;
  padding: ${SPACE_LG}px;
  border-radius: ${SURFACE_CARD_RADIUS_LARGE}px;
  background-color: ${COLOR_WHITE};
  box-shadow: ${ELEVATION_1};

  ${TABLET_QUERY} {
    padding: ${SPACE_XL}px;
    border-radius: ${SURFACE_CARD_RADIUS}px;
  }
`;

const NextLessonCell = styled.button`
  display: block;
  width: 100%;
  padding: 0;
  border: none;
  background: none;
  text-align: left;
`;

const StartNextLessonButton = styled(PlayButton)`
  max-width: none;
  margin-top: ${SPACE_LG}px;
`;

export const LessonPageEndLessonScreen: React.FC<Props> = ({
  lesson,
  setLessonExecutionState,
  account,
}) => {
  const history = useHistory();

  const { t } = useTranslation();
  const { forceReloadProfileState } = React.useContext(AccountContext);
  const { obtainNextLesson } = React.useContext(ApiContext);
  const [nextLessonData, setNextLessonData] =
    React.useState<NextLessonResponse | null>(null);
  const { startLoading, stopLoading } = useLoadingIndicator(false);
  const { logEvent } = React.useContext(AnalyticsContext);
  const shouldLogViewLessonEnd = React.useRef<boolean>(true);
  const [showPrintables, setShowPrintables] = React.useState(false);

  React.useEffect(() => {
    startLoading();
    forceReloadProfileState();
  }, [startLoading, forceReloadProfileState]);

  React.useEffect(() => {
    if (nextLessonData === null && account.hasMaybeProfileState()) {
      obtainNextLesson(
        lesson.id,
        account.profile!!.id,
        lesson.version,
        lesson.custom_flashpack_id,
        account.profileState?.lessons_in_progress ?? {}
      ).then((nextLessonResponse) => {
        setNextLessonData(nextLessonResponse);
        stopLoading();
      });
    }
  }, [
    nextLessonData,
    lesson,
    account,
    startLoading,
    stopLoading,
    obtainNextLesson,
  ]);

  if (nextLessonData === null) {
    return <></>;
  }

  if (shouldLogViewLessonEnd.current) {
    logEvent(new ViewLessonEndEvent(lesson.id, lesson.title));
    shouldLogViewLessonEnd.current = false;
  }

  const currentLesson = nextLessonData.current_lesson;
  const nextLesson: Lesson | undefined = nextLessonData.suggested_lessons[0];

  const navigateToLesson = (target: Lesson) => {
    setLessonExecutionState({ type: "BEFORE_PLAY_LESSON_NOT_YET_STARTED" });
    if (
      target.id !== currentLesson.id ||
      target.custom_flashpack_id !== currentLesson.custom_flashpack_id
    ) {
      // We are staying in the same functional component, so pushing would result in bad
      // back behavior
      history.replace(
        target.custom_flashpack_id
          ? `/lessons/${target.id}/${target.slug}/flashpacks/${target.custom_flashpack_id}/`
          : `/lessons/${target.id}/${target.slug}/`
      );
    }
  };

  const isCompleted = didProfileCompleteLesson(
    account.profileState?.completed_lessons_info || {},
    currentLesson.id,
    currentLesson.custom_flashpack_id
  );
  const showsGems =
    !currentLesson.is_studio_project &&
    currentLesson.gems_earned > 1 &&
    !isInTrialMode(account.user);
  const unlockedStickerCount = railStickerAwards(currentLesson, account).filter(
    (award) => account.profileState?.unlocked_award_uuids.includes(award.uuid)
  ).length;
  const gemsText = showsGems
    ? t("profile_stat_gems_value", { count: currentLesson.gems_earned })
    : null;
  const stickersText =
    unlockedStickerCount > 0
      ? t("end_lesson_stickers_count", { count: unlockedStickerCount })
      : null;
  const earnedSummary =
    gemsText && stickersText
      ? t("end_lesson_earned_two", { first: gemsText, second: stickersText })
      : gemsText || stickersText
        ? t("end_lesson_earned_one", { first: gemsText ?? stickersText })
        : null;

  return (
    <>
      <BackNavRow label={t("lesson_nav_back")} />
      <Column>
        <DetailsTitle>{t("end_lesson_complete")}</DetailsTitle>
        {earnedSummary && <EarnedSummary>{earnedSummary}</EarnedSummary>}
        {showsGems && (
          <GemsHero
            src={`${process.env.PUBLIC_URL}/end_lesson/gems-hero.svg`}
            alt=""
          />
        )}
        <Awards
          lesson={currentLesson}
          account={account}
          isCentered
          trailing={
            <CompletionBurst
              src={`${process.env.PUBLIC_URL}/${
                isCompleted ? "check-green.png" : "check-light-grey.png"
              }`}
              alt=""
            />
          }
        />
        <Actions>
          {currentLesson.downloadable_materials.length > 0 && (
            <PillButton
              onClick={() => {
                logEvent(
                  new LessonMaterialsPostLessonButtonTapEvent(
                    lesson.id,
                    lesson.title
                  )
                );
                setShowPrintables(true);
              }}
            >
              <FontAwesomeIcon icon={faCopy} />
              {t("end_lesson_view_printables_button")}
            </PillButton>
          )}
          <ActionRow>
            <PillButton
              onClick={() => {
                logEvent(
                  new LessonEndPlayAgainButtonTapEvent(lesson.id, lesson.title)
                );
                navigateToLesson(currentLesson);
              }}
            >
              <FontAwesomeIcon icon={faRotateLeft} />
              {t("end_lesson_do_it_again")}
            </PillButton>
            <PillButton
              onClick={() => {
                logEvent(
                  new LessonEndFinishButtonTapEvent(lesson.id, lesson.title)
                );
                history.push(TabEnum.Home);
              }}
            >
              <FontAwesomeIcon icon={faCircleCheck} />
              {t("end_lesson_finish")}
            </PillButton>
          </ActionRow>
        </Actions>
        {nextLesson && (
          <NextLessonCard>
            <NextLessonCell
              onClick={() => {
                logEvent(
                  new LessonEndNextLessonImageTapEvent(
                    lesson.id,
                    lesson.title,
                    nextLesson.id,
                    nextLesson.title
                  )
                );
                navigateToLesson(nextLesson);
              }}
            >
              <LessonItemBody
                lesson={nextLesson}
                account={account}
                leftCornerDecoration={LeftCornerDecoration.STANDARD}
              />
            </NextLessonCell>
            <StartNextLessonButton
              onClick={() => {
                logEvent(
                  new LessonEndPlayNextButtonTapEvent(
                    lesson.id,
                    lesson.title,
                    nextLesson.id,
                    nextLesson.title
                  )
                );
                navigateToLesson(nextLesson);
              }}
            >
              <FontAwesomeIcon icon={faPlay} />
              {t("end_lesson_start_next_lesson")}
            </StartNextLessonButton>
          </NextLessonCard>
        )}
      </Column>
      <PrintablesModal
        lesson={currentLesson}
        show={showPrintables}
        onHide={() => setShowPrintables(false)}
      />
    </>
  );
};
